// Launch imports this module before the list hooks, and collections read
// their rows in the order they are created: importing one here would put the
// membership read, which every settings screen waits on, behind every list.
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import type { Membership } from "@/lib/collections/membership";
import { membershipCollection } from "@/lib/collections/membership";
import type { Staff, StaffWithMembership } from "@/lib/collections/staff";
import { staffCollection } from "@/lib/collections/staff";

/**
 * Every staff row in the local (synced) DB, unfiltered.
 *
 * `isLoading` is returned alongside the rows, unlike {@link useAllProperties}:
 * a caller that narrows by staff has to tell "no rows yet" from "no matches",
 * and answering that from an empty array alone silently filters everything
 * out while the collection is still streaming.
 */
export function useAllStaff(): { isLoading: boolean; staff: Staff[] } {
  const { data = [], isLoading } = useLiveQuery(
    (q) => q.from({ s: staffCollection }).select(({ s }) => s),
    []
  );
  return { isLoading, staff: data };
}

/** Every membership row in the local (synced) DB, unfiltered. */
export function useAllMemberships(): {
  isLoading: boolean;
  memberships: Membership[];
} {
  const { data = [], isLoading } = useLiveQuery(
    (q) => q.from({ m: membershipCollection }).select(({ m }) => m),
    []
  );
  return { isLoading, memberships: data };
}

/** A property's members, each with their role and when they joined. */
export function usePropertyMembers(propertyId: string): StaffWithMembership[] {
  const { data: rows = [] } = useLiveQuery(
    (q) =>
      q
        .from({ m: membershipCollection })
        // Inner join: exclude memberships whose staff row isn't synced locally
        // (scoped out, still syncing, or orphaned) instead of rendering a member
        // with no id/name/email.
        .join({ s: staffCollection }, ({ m, s }) => eq(s.id, m.userId))
        .where(({ m }) => eq(m.propertyId, propertyId))
        .select(({ m, s }) => ({
          joinedAt: m.createdAt,
          role: m.role,
          staff: s,
        })),
    [propertyId]
  );

  // The query builder types each joined column as possibly-undefined; it can't
  // see that the inner join guarantees a staff row. Resolve the joined entity to
  // its domain type, drop any staff-less rows, then merge in role + joinedAt.
  return useMemo(
    () =>
      rows.flatMap((row) => {
        const staff = row.staff as Staff | undefined;
        return staff
          ? [{ ...staff, joinedAt: row.joinedAt, role: row.role }]
          : [];
      }),
    [rows]
  );
}
